<template>
  <!-- Sidebar card -->
  <aside class=" bg-[#e7d7bf] shadow-sm ring-1 ring-[#e7d8c8] overflow-hidden">
    <!-- Mobile toggle header -->
    <button
      type="button"
      @click="isCollapsed = !isCollapsed"
      class="flex w-full items-center justify-between bg-[#F7941F] px-4 py-3 lg:hidden focus:outline-none focus:ring-0"
    >
      <span class="text-lg font-black uppercase italic tracking-wider text-white">{{ t('materials.filters') }}</span>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="h-6 w-6 text-white transition-transform duration-200"
        :class="{ 'rotate-180': !isCollapsed }"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path v-if="isCollapsed" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
        <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>

    <div :class="{ 'hidden lg:block': isCollapsed }">
      <!-- Header / controls -->

      <div class="bg-gradient-to-br from-mm-orangefrom to-mm-orangeto p-6">
      <!-- Zoeken -->
      <div class="mb-4">
        <label class="sr-only" for="mm-q">{{ t('materials.searchLabel') }}</label>
        <input
          id="mm-q"
          v-model="local.query"
          type="text"
          :placeholder="t('materials.searchPlaceholder')"
          class="h-[44px] w-full border border-transparent bg-white/95 px-4 py-3 text-sm shadow-inner placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-black/10"
        />
      </div>
      <!-- Locatie + radius -->
      <div class="grid grid-cols-1 items-center gap-4">
        <!-- Locatie autocomplete (met icoon) -->
        <div class="relative">
          <MmLocationAutocomplete
            :modelValue="local.locationData"
            :placeholder="t('materials.locationPlaceholder')"
            :initialText="local.locationText"
            inputClass="h-[44px] w-full border border-transparent bg-white/95 px-4 pr-10 py-3 text-sm shadow-inner placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-black/10"
            @update:modelValue="onLocationSelect"
            @clear="onLocationClear"
          />
          <button
              type="button"
              @click="locateMe"
              class="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-lg bg-white/90 shadow-inner ring-1 ring-black/5 hover:bg-white leading-none focus:outline-none focus:ring-0 z-10"
              :aria-label="t('aria.locateMe')"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
              <path id="location-crosshairs-sharp-regular-full"
                    d="M58.882,48v1.813a8.237,8.237,0,0,1,7.305,7.305H68v1.765H66.188a8.237,8.237,0,0,1-7.305,7.305V68H57.118V66.188a8.237,8.237,0,0,1-7.305-7.305H48V57.118h1.813a8.237,8.237,0,0,1,7.305-7.305V48Zm5.588,10A6.471,6.471,0,1,0,58,64.471,6.471,6.471,0,0,0,64.471,58Zm-4.118,0A2.353,2.353,0,1,0,58,60.353,2.355,2.355,0,0,0,60.353,58Zm-6.471,0A4.118,4.118,0,1,1,58,62.118,4.116,4.116,0,0,1,53.882,58Z"
                    transform="translate(-48 -48)"/>
            </svg>

          </button>
        </div>

        <!-- Radius dropdown -->
        <div class="relative w-full">
          <select
            v-model.number="local.radius"
            class="h-[44px] w-full border border-transparent bg-white/95 px-3 py-3 text-sm shadow-inner focus:outline-none focus:ring-2 focus:ring-black/10 appearance-none cursor-pointer"
          >
            <option :value="5">5 {{ t('materials.km') }}</option>
            <option :value="10">10 {{ t('materials.km') }}</option>
            <option :value="25">25 {{ t('materials.km') }}</option>
            <option :value="50">50 {{ t('materials.km') }}</option>
            <option :value="100">100 {{ t('materials.km') }}</option>
          </select>
          <svg class="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" fill="none"
               stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
          </svg>
        </div>
      </div>
    </div>

    <!-- Type filter -->
    <div class="px-4 pt-4 pb-0 border-b-[0.5px] border-[#eedcc5]">
      <div class="flex gap-2 mb-4">
        <button
          type="button"
          @click="local.type = local.type === 'aangeboden' ? '' : 'aangeboden'"
          class="flex-1 h-12 border-none flex items-center justify-start px-4 gap-2 transition-all shadow-sm outline-none focus:outline-none focus:ring-0"
          :class="local.type === 'aangeboden' ? 'bg-white' : 'bg-white hover:bg-neutral-50'"
        >
          <div class="w-4 h-4 rounded-full border border-mm-orange flex items-center justify-center">
            <div v-if="local.type === 'aangeboden'" class="w-2.5 h-2.5 rounded-full bg-mm-orange"></div>
          </div>
          <span class="text-[15px] font-bold text-black">{{ t('materials.offered') || 'Aangeboden' }}</span>
        </button>
        <button
          type="button"
          @click="local.type = local.type === 'gevraagd' ? '' : 'gevraagd'"
          class="flex-1 h-12 border-none flex items-center justify-start px-4 gap-2 transition-all shadow-sm outline-none focus:outline-none focus:ring-0"
          :class="local.type === 'gevraagd' ? 'bg-white' : 'bg-white hover:bg-neutral-50'"
        >
          <div class="w-4 h-4 rounded-full border border-black/20 flex items-center justify-center">
            <div v-if="local.type === 'gevraagd'" class="w-2.5 h-2.5 rounded-full bg-mm-orange"></div>
          </div>
          <span class="text-[15px] font-bold text-black">{{ t('materials.requested') || 'Gevraagd' }}</span>
        </button>
      </div>
    </div>

    <!-- Categorieën -->
    <div class="p-4">
      <ul class="space-y-2 list-none pl-0 ml-0 mmf-list">
        <li v-for="c in categories" :key="c.id">
          <button
            @click="toggleCategory(c.id)"
            class="flex w-full items-center justify-between rounded-full bg-white px-3 py-2 shadow-sm hover:bg-neutral-50 focus:outline-none focus:ring-0"
          >
            <span class="flex items-center gap-3 text-left">
              <span class="inline-flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-neutral-100 ring-1 ring-neutral-200">
                <img v-if="categoryImage(c)" :src="categoryImage(c)" :alt="c.name" class="h-full w-full object-cover" loading="lazy" decoding="async" />
                <img v-else :src="catPlaceholder" :alt="c.name" class="h-full w-full object-cover" loading="lazy" decoding="async" />
              </span>
              <span>{{ c.name }}</span>
            </span>
            <span
              class="ml-3 inline-flex items-center justify-center rounded-full px-2 py-1 min-w-[3.25rem]"
              :class="isSelected(c.id) ? 'bg-orange-200 text-black' : 'bg-neutral-100 text-neutral-700'"
              style="font-size: 18px;"
            >
              ({{ c.count }})
            </span>
          </button>
        </li>
      </ul>
    </div>
  </div>
  </aside>
</template>

<script setup>
import { reactive, watch, ref } from 'vue'
import { t as i18nT } from '../i18n'
import MmLocationAutocomplete from './MmLocationAutocomplete.vue'

const t = (key) => i18nT(key)

const isCollapsed = ref(true)

// Placeholder image used for category avatars when no image is available
const catPlaceholder = 'data:image/svg+xml;utf8,' + encodeURIComponent(`<?xml version="1.0" encoding="UTF-8"?><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><rect width="40" height="40" fill="#e5e7eb"/><circle cx="20" cy="20" r="12" fill="#d1d5db"/></svg>`)

// Helpers to derive a category image URL with graceful fallbacks (similar to MmCategories.vue)
const imageFieldCandidates = ['image', 'thumbnail', 'term_image', 'icon'];

function getImageFromAcf(acf) {
  if (!acf || typeof acf !== 'object') return '';
  for (const key of imageFieldCandidates) {
    const img = acf[key];
    if (!img) continue;
    if (typeof img === 'string') return img;
    if (img?.sizes?.medium) return img.sizes.medium;
    if (img?.url) return img.url;
  }
  return '';
}

function categoryImage(cat) {
  // prefer explicit image_url, then any precomputed imageUrl, then ACF or legacy fields
  return cat?.image_url || cat?.imageUrl || getImageFromAcf(cat?.acf) || cat?.image || cat?.thumbnail || '';
}

function initials(name) {
  if (!name) return '';
  return String(name)
    .split(/\s+/)
    .slice(0, 2)
    .map(p => p.charAt(0).toUpperCase())
    .join('');
}

const props = defineProps({
  categories: { type: Array, default: () => [] },
  modelValue: { type: Object, default: () => ({ query: '', locationText: '', radius: 25, selectedCats: [], type: '', locationData: null }) },
})

const emit = defineEmits(['update:modelValue', 'filters-change', 'locate-me'])

const local = reactive({
  query: props.modelValue?.query ?? '',
  locationText: props.modelValue?.locationText ?? '',
  radius: props.modelValue?.radius ?? 25,
  selectedCats: Array.isArray(props.modelValue?.selectedCats) ? [...props.modelValue.selectedCats] : [],
  type: props.modelValue?.type ?? '',
  locationData: props.modelValue?.locationData ?? null,
})

function onLocationSelect(item) {
  if (item) {
    local.locationData = item
    local.locationText = item.display_name || item.place_name || ''
  }
}

function onLocationClear() {
  local.locationData = null
  local.locationText = ''
}

watch(local, () => {
  const payload = { ...local, selectedCats: [...local.selectedCats] }
  emit('update:modelValue', payload)
  emit('filters-change', payload)
}, { deep: true })

// Keep local UI in sync with parent updates
watch(() => props.modelValue?.locationText, (newVal) => {
  if (newVal !== undefined && newVal !== local.locationText) {
    local.locationText = newVal
  }
})

watch(() => props.modelValue?.query, (newVal) => {
  if (newVal !== undefined && newVal !== local.query) {
    local.query = newVal
  }
})

watch(() => props.modelValue?.radius, (newVal) => {
  if (newVal !== undefined && newVal !== local.radius) {
    local.radius = newVal
  }
})

// Specifically watch locationData for changes from autocomplete
watch(() => local.locationData, (newVal) => {
  if (newVal) {
    local.locationText = newVal.display_name || newVal.place_name || ''
  }
})

watch(() => props.modelValue?.type, (newVal) => {
  if (newVal !== undefined && newVal !== local.type) {
    local.type = newVal
  }
})

// Keep local UI in sync with parent-enforced single selection
watch(() => props.modelValue?.selectedCats, (arr) => {
  const flat = Array.isArray(arr) ? arr.map(n => Number(n)).filter(n => Number.isFinite(n) && n > 0) : []
  const coerced = flat.length > 1 ? [flat[0]] : flat
  const sameLen = coerced.length === local.selectedCats.length
  const sameVals = sameLen && coerced.every((v, i) => v === local.selectedCats[i])
  if (!sameVals) {
    local.selectedCats.splice(0, local.selectedCats.length, ...coerced)
  }
})

function isSelected(id){
  return local.selectedCats.includes(id)
}

function toggleCategory(id){
  const nid = Number(id)
  if (!Number.isFinite(nid) || nid <= 0) return
  const i = local.selectedCats.indexOf(nid)
  if (i > -1) {
    // Deselect when clicking the same category
    local.selectedCats.splice(i, 1)
  } else {
    // Enforce single selection: replace with the one clicked
    local.selectedCats.splice(0, local.selectedCats.length, nid)
  }
}

function locateMe(){
  emit('locate-me')
}
</script>


<style scoped>
/* Force-remove any bullets or theme-added markers for the categories list */
.mmf-list,
.mmf-list li {
  list-style: none !important;
  padding-left: 0 !important;
  margin-left: 0 !important;
}
.mmf-list li::marker { content: '' !important; }
.mmf-list li::before { content: none !important; display: none !important; }
</style>
